import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { PageHeader, EmptyState, Chip, LoadingState, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    CheckSquare, Users, ListChecks, Question, CalendarBlank, CheckCircle, ChartBar,
    Globe, Buildings, HouseLine, UsersThree, Trophy, Scales, Lock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const VOTE_TYPES = [
    { value: "election", label: "Committee Election", icon: Users },
    { value: "poll", label: "Poll", icon: Question },
    { value: "resolution", label: "Resolution", icon: ListChecks },
];
const PHASE_META = {
    draft: { label: "Draft", variant: "neutral" },
    upcoming: { label: "Upcoming", variant: "warn" },
    active: { label: "Active", variant: "success" },
    closed: { label: "Closed", variant: "default" },
    cancelled: { label: "Cancelled", variant: "danger" },
};
const AUDIENCE_ICON = { ALL: Globe, BLOCK: Buildings, FLAT: HouseLine, COMMITTEE_ADMIN: UsersThree };
const AUDIENCE_LABEL_BASE = { ALL: "Everyone", BLOCK: "Block", FLAT: "Flat", COMMITTEE_ADMIN: "Committee & Admin" };

function typeInfo(t) { return VOTE_TYPES.find(v => v.value === t) || VOTE_TYPES[0]; }
function fmt(dt) {
    if (!dt) return "—";
    try { return new Date(dt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return dt; }
}
function audienceLabel(aud) {
    if (!aud) return "Everyone";
    if (aud.type === "BLOCK") return `Block ${aud.block}`;
    if (aud.type === "FLAT") return "Your Flat";
    return AUDIENCE_LABEL_BASE[aud.type] || "Everyone";
}

// ---------- Candidate avatar (secure blob fetch — never an unauthenticated <img src="/api/files/...">) ----------
function CandidateAvatar({ voteId, candidateId, photoFileId, name, size = 48 }) {
    const [src, setSrc] = useState(null);
    const [err, setErr] = useState(false);
    useEffect(() => {
        if (!photoFileId) return;
        let revoked = null, cancelled = false;
        (async () => {
            try {
                const res = await api.get(`/votes/${voteId}/candidates/${candidateId}/photo`, { responseType: "blob" });
                if (cancelled) return;
                const url = URL.createObjectURL(res.data);
                revoked = url;
                setSrc(url);
            } catch { if (!cancelled) setErr(true); }
        })();
        return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
    }, [voteId, candidateId, photoFileId]);
    if (!photoFileId || err || !src) {
        return (
            <div style={{ width: size, height: size }} className="rounded-full bg-brand-sage text-brand-ink font-heading flex items-center justify-center shrink-0">
                {(name || "?").charAt(0).toUpperCase()}
            </div>
        );
    }
    return <img src={src} alt={name || "candidate"} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0 border border-brand-line" />;
}

// ---------- Cast Vote Dialog ----------
function CastVoteDialog({ vote, onClose, onCast }) {
    const [selected, setSelected] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const info = typeInfo(vote.type);

    function toggle(id) {
        if (vote.type === "election") {
            const max = vote.max_selections || 1;
            if (selected.includes(id)) { setSelected(selected.filter(x => x !== id)); return; }
            if (selected.length >= max) { toast.info(`You can select up to ${max} candidate(s)`); return; }
            setSelected([...selected, id]);
        } else {
            setSelected([id]);
        }
    }

    async function submit() {
        if (selected.length === 0) { toast.error("Please make a selection"); return; }
        setSubmitting(true);
        try {
            await api.post(`/votes/${vote.id}/cast`, { selected_option_ids: selected });
            toast.success("Your vote has been recorded");
            onCast?.();
            onClose();
        } catch (e) { toast.error(formatError(e)); }
        finally { setSubmitting(false); }
    }

    const choices = vote.type === "election" ? (vote.candidates || []) : (vote.options || []);

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="cast-vote-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2"><info.icon size={22} className="text-brand-action" /> {vote.title}</DialogTitle>
                    <DialogDescription>{vote.description}</DialogDescription>
                </DialogHeader>

                <div className="bg-brand-bg border border-brand-line rounded-sm p-3 text-xs flex items-center gap-1.5">
                    <CalendarBlank size={14} weight="duotone" className="text-brand-action" />
                    <span className="text-brand-inkSoft">Voting closes {fmt(vote.end_date)}</span>
                </div>

                {vote.type === "election" && (
                    <div className="space-y-2.5 mt-3">
                        <p className="text-sm font-medium text-brand-ink">Select up to {vote.max_selections || 1} candidate(s):</p>
                        {choices.map(c => {
                            const isSelected = selected.includes(c.id);
                            return (
                                <div key={c.id} onClick={() => toggle(c.id)} data-testid={`cast-candidate-${c.id}`}
                                    className={`p-3 border-2 rounded-sm cursor-pointer transition-colors flex items-start gap-3 ${isSelected ? "border-brand-action bg-brand-sage/40" : "border-brand-line hover:border-brand-action/50"}`}>
                                    <CandidateAvatar voteId={vote.id} candidateId={c.id} photoFileId={c.photo_file_id} name={c.name} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <h4 className="font-heading text-base text-brand-ink truncate">{c.name}</h4>
                                            {isSelected && <CheckCircle size={20} weight="fill" className="text-brand-action shrink-0" />}
                                        </div>
                                        {c.position && <p className="text-xs text-brand-action font-medium">{c.position}</p>}
                                        {c.bio && <p className="text-xs text-brand-inkSoft mt-1">{c.bio}</p>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {(vote.type === "poll" || vote.type === "resolution") && (
                    <div className="space-y-2.5 mt-3">
                        <p className="text-sm font-medium text-brand-ink">Select your choice:</p>
                        {choices.map(o => {
                            const isSelected = selected.includes(o.id);
                            return (
                                <div key={o.id} onClick={() => toggle(o.id)} data-testid={`cast-option-${o.id}`}
                                    className={`p-3.5 border-2 rounded-sm cursor-pointer transition-colors flex items-center justify-between ${isSelected ? "border-brand-action bg-brand-sage/40" : "border-brand-line hover:border-brand-action/50"}`}>
                                    <span className="font-medium text-brand-ink">{o.text}</span>
                                    {isSelected && <CheckCircle size={20} weight="fill" className="text-brand-action" />}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="bg-[#FFF7ED] text-[#EA580C] border border-[#FFEDD5] rounded-lg p-3 text-xs flex items-center gap-1.5 mt-3">
                    <Lock size={13} weight="fill" /> One vote per flat. Once submitted, your ballot cannot be changed.
                </div>

                <DialogFooter>
                    <Button variant="outline" className="rounded-full border-brand-line" onClick={onClose}>Cancel</Button>
                    <Button data-testid="cast-vote-submit-btn" disabled={selected.length === 0 || submitting} onClick={submit} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                        <CheckCircle size={16} className="mr-1.5" /> Cast Vote
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Results Dialog (read-only) ----------
function ResultsViewDialog({ vote, onClose }) {
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try { const r = await api.get(`/votes/${vote.id}/results`); setResults(r.data); }
            catch (e) { toast.error(formatError(e)); onClose(); }
            finally { setLoading(false); }
        })();
        // eslint-disable-next-line
    }, [vote.id]);

    const choices = vote.type === "election" ? (vote.candidates || []) : (vote.options || []);

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="vote-results-view-dialog">
                <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">{vote.title}</DialogTitle></DialogHeader>
                {loading || !results ? <LoadingState message="Loading results..." /> : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <StatCard label="Total Votes" value={results.total_votes} testId="rv-total-votes" />
                            <StatCard label="Participation" value={`${results.participation_rate}%`} accent testId="rv-participation" />
                            <StatCard label="Eligible Flats" value={results.eligible_flats} testId="rv-eligible-flats" />
                        </div>

                        {vote.type === "election" && results.phase === "closed" && (
                            results.is_tie ? (
                                <div className="rounded-lg px-4 py-3 bg-[#FFF7ED] text-[#EA580C] border border-[#FFEDD5] flex items-center gap-2 text-sm font-medium" data-testid="rv-tie-banner">
                                    <Scales size={18} weight="fill" /> Tie — No winner determined.
                                </div>
                            ) : results.winner_candidate_id ? (
                                <div className="rounded-lg px-4 py-3 bg-[#ECFDF5] text-[#059669] border border-[#A7F3D0] flex items-center gap-2 text-sm font-medium" data-testid="rv-winner-banner">
                                    <Trophy size={18} weight="fill" /> Winner: {choices.find(c => c.id === results.winner_candidate_id)?.name || "—"}
                                </div>
                            ) : null
                        )}

                        {vote.type === "resolution" && results.resolution && (
                            <div className={`rounded-lg px-4 py-3 text-sm font-medium capitalize ${
                                results.resolution.outcome === "passed" ? "bg-[#ECFDF5] text-[#059669] border border-[#A7F3D0]" :
                                results.resolution.outcome === "failed" ? "bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]" : "bg-brand-bg text-brand-inkSoft border border-brand-line"
                            }`} data-testid="rv-resolution-outcome">Outcome: {results.resolution.outcome}</div>
                        )}

                        <div className="space-y-2">
                            {choices.map(c => {
                                const r = results.results[c.id] || { count: 0, percentage: 0 };
                                return (
                                    <div key={c.id} className="bg-brand-bg border border-brand-line rounded-sm p-3" data-testid={`rv-row-${c.id}`}>
                                        <div className="flex items-center justify-between mb-1.5">
                                            <div className="flex items-center gap-2 min-w-0">
                                                {vote.type === "election" && <CandidateAvatar voteId={vote.id} candidateId={c.id} photoFileId={c.photo_file_id} name={c.name} size={28} />}
                                                <span className="font-medium text-brand-ink text-sm truncate">{vote.type === "election" ? c.name : c.text}</span>
                                            </div>
                                            <span className="text-xs text-brand-inkSoft shrink-0">{r.count} votes ({r.percentage}%)</span>
                                        </div>
                                        <div className="w-full bg-white rounded-full h-2 overflow-hidden">
                                            <div className="bg-brand-action h-2 rounded-full transition-all" style={{ width: `${r.percentage}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                <DialogFooter><Button variant="outline" className="rounded-full border-brand-line" onClick={onClose}>Close</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Vote Card ----------
function VoteCard({ vote, onCast, onResults }) {
    const info = typeInfo(vote.type);
    const Icon = info.icon;
    const AudIcon = AUDIENCE_ICON[vote.audience?.type] || Globe;
    const phaseMeta = PHASE_META[vote.phase] || PHASE_META.active;
    const canCast = vote.phase === "active" && !vote.user_flat_voted;
    const canViewResults = vote.phase === "closed" || vote.show_results_before_end;

    return (
        <div className="bg-white border border-brand-line rounded-sm p-4 hover:-translate-y-0.5 hover:shadow-lg transition-transform duration-200" data-testid={`vote-card-${vote.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                    <Icon size={20} weight="duotone" className="text-brand-action shrink-0" />
                    <div className="min-w-0">
                        <h3 className="font-heading text-base tracking-tight text-brand-ink truncate">{vote.title}</h3>
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{info.label}</p>
                    </div>
                </div>
                {vote.user_flat_voted ? (
                    <Chip variant="success" data-testid={`vote-voted-chip-${vote.id}`}><CheckCircle size={11} weight="fill" className="mr-1 inline" /> Voted</Chip>
                ) : (
                    <Chip variant={phaseMeta.variant}>{phaseMeta.label}</Chip>
                )}
            </div>

            <p className="text-sm text-brand-inkSoft mb-3 line-clamp-2">{vote.description}</p>

            <div className="flex flex-wrap gap-3 text-[11px] text-brand-inkSoft mb-3">
                <span className="flex items-center gap-1"><CalendarBlank size={12} weight="duotone" /> {vote.phase === "upcoming" ? `Starts ${fmt(vote.start_date)}` : `Ends ${fmt(vote.end_date)}`}</span>
                <span className="flex items-center gap-1"><AudIcon size={12} weight="duotone" /> {audienceLabel(vote.audience)}</span>
                <span className="flex items-center gap-1"><ChartBar size={12} weight="duotone" /> {vote.total_votes} votes cast</span>
            </div>

            <div className="flex gap-2 border-t border-brand-line pt-3">
                {canCast && <Button size="sm" onClick={() => onCast(vote)} className="flex-1 rounded-full bg-brand-action hover:bg-brand-actionHover text-white" data-testid={`vote-cast-btn-${vote.id}`}>Cast Vote</Button>}
                {canViewResults && <Button size="sm" variant="outline" onClick={() => onResults(vote)} className="flex-1 rounded-full border-brand-line" data-testid={`vote-view-results-btn-${vote.id}`}><ChartBar size={13} className="mr-1" /> View Results</Button>}
                {!canCast && !canViewResults && vote.phase === "upcoming" && <p className="text-xs text-brand-inkSoft py-1.5">Voting hasn't started yet.</p>}
            </div>
        </div>
    );
}

// ---------- Main Page ----------
export default function VotingDashboard() {
    const [votes, setVotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("active");
    const [castVote, setCastVote] = useState(null);
    const [resultsVote, setResultsVote] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    async function loadVotes() {
        try { const r = await api.get("/votes"); setVotes(r.data); }
        catch (e) { toast.error(formatError(e)); }
    }
    useEffect(() => { (async () => { setLoading(true); await loadVotes(); setLoading(false); })(); }, []);

    // Notification deep-link: /app/voting?id=<vote_id>
    useEffect(() => {
        const openId = searchParams.get("id");
        if (!openId || loading) return;
        const vote = votes.find(v => v.id === openId);
        if (vote) {
            if (vote.phase === "active" && !vote.user_flat_voted) setCastVote(vote);
            else if (vote.phase === "closed" || vote.show_results_before_end) setResultsVote(vote);
            setTab(vote.phase === "upcoming" ? "upcoming" : vote.phase === "closed" ? "past" : "active");
        }
        setSearchParams({}, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, votes]);

    const grouped = useMemo(() => ({
        active: votes.filter(v => v.phase === "active"),
        upcoming: votes.filter(v => v.phase === "upcoming"),
        past: votes.filter(v => v.phase === "closed"),
    }), [votes]);

    if (loading) return <LoadingState message="Loading votes..." />;

    const list = grouped[tab] || [];

    return (
        <div className="animate-fade-up space-y-6" data-testid="voting-dashboard-page">
            <PageHeader overline="Governance" title="Voting" description="Participate in your society's elections, resolutions, and polls." />

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="vote-tabs">
                    <TabsTrigger value="active" data-testid="vote-tab-active">Active {grouped.active.length > 0 && `(${grouped.active.length})`}</TabsTrigger>
                    <TabsTrigger value="upcoming" data-testid="vote-tab-upcoming">Upcoming {grouped.upcoming.length > 0 && `(${grouped.upcoming.length})`}</TabsTrigger>
                    <TabsTrigger value="past" data-testid="vote-tab-past">Past</TabsTrigger>
                </TabsList>
            </Tabs>

            {list.length === 0 ? (
                <EmptyState
                    title={tab === "active" ? "No active votes" : tab === "upcoming" ? "Nothing scheduled" : "No past votes yet"}
                    description={tab === "active" ? "Check back once your society opens a new vote." : tab === "upcoming" ? "Upcoming elections, polls and resolutions will show up here." : "Closed votes will appear here."}
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vote-list">
                    {list.map(vote => <VoteCard key={vote.id} vote={vote} onCast={setCastVote} onResults={setResultsVote} />)}
                </div>
            )}

            {castVote && <CastVoteDialog vote={castVote} onClose={() => setCastVote(null)} onCast={loadVotes} />}
            {resultsVote && <ResultsViewDialog vote={resultsVote} onClose={() => setResultsVote(null)} />}
        </div>
    );
}
